import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import moment from 'moment';
import Loading from 'react-fullscreen-loading';
import { AuthContext } from '../../../component/context/AuthContext';
import * as CONST from '../../../component/config/Constant';
import DivSpinner from '../../../component/utilities/Spinner';
import ApplyResignationForm from './ApplyResignationForm';
import HeaderSection from '../banner/HeaderSection';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { FiCheckCircle, FiClock, FiXCircle, FiAlertCircle, FiCalendar, FiFileText, FiUser, FiArrowRight } from 'react-icons/fi';

function Resignation() {
  const [authUserData] = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [resignation, setResignation] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [prevResignation, setPrevResignation] = useState(null);
  let isToken = '';
  if (authUserData && authUserData[0]) {
    isToken = authUserData[0].token || "";
  }

  useEffect(() => { fetchMyResignations(); }, []);

  const fetchMyResignations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(CONST.GET_MY_RESIGNATIONS, {
        headers: { Authorization: `Bearer ${isToken}` },
      });
      if (response.data.status === 'success' && response.data.data && response.data.data.length > 0) {
        setResignation(response.data.data[0]);
        setPrevResignation(response.data.data[1] || null);
      } else {
        setResignation(null);
        setPrevResignation(null);
      }
    } catch (error) {
      setResignation(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelResignation = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to withdraw your resignation?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, withdraw it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await axios.post(CONST.CANCEL_RESIGNATION, {
            resignation_id: resignation.id,
            step_number: resignation.current_step,
            action: "Withdrawn",
            comment: "Resignation withdrawn by employee.",
          }, { headers: { Authorization: `Bearer ${isToken}` } });
          if (response.data.status === "success") {
            toast.success("Resignation withdrawn successfully.");
            fetchMyResignations();
          } else {
            toast.error(response.data.message);
          }
        } catch (error) {
          toast.error(error.response?.data?.message || "An error occurred while withdrawing.");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleApplyClick = () => setShowForm(true);
  const onFormClose = () => { setShowForm(false); fetchMyResignations(); };

  const getStatusIcon = (status) => {
    if (status === "Approved") return <FiCheckCircle size={16} style={{ color: '#059669' }} />;
    if (status === "Pending") return <FiClock size={16} style={{ color: '#d97706' }} />;
    if (status === "Rejected") return <FiXCircle size={16} style={{ color: '#dc2626' }} />;
    return <FiAlertCircle size={16} style={{ color: '#94a3b8' }} />;
  };

  const getStatusStyle = (status) => {
    if (status === "Approved") return { bg: '#ecfdf5', color: '#059669' };
    if (status === "Pending") return { bg: '#fffbeb', color: '#d97706' };
    if (status === "Rejected") return { bg: '#fef2f2', color: '#dc2626' };
    if (status === "Withdrawn") return { bg: '#f1f5f9', color: '#64748b' };
    if (status === "Info Requested") return { bg: '#fff7ed', color: '#c2410c' };
    if (status === "Info Provided") return { bg: '#f0f9ff', color: '#0369a1' };
    return { bg: '#f1f5f9', color: '#64748b' };
  };

  const finalStyle = getStatusStyle(resignation?.final_status);

  // No resignation or withdrawn/rejected — show apply
  const canApply = !resignation || resignation.final_status === "Withdrawn" || resignation.final_status === "Rejected";

  if (loading && !resignation && !showForm) {
    return (
      <>
        <HeaderSection title="My" heading="Resignation" />
        <div className="page-body"><div className="container-xl text-center py-5"><DivSpinner size="lg" color="primary" /></div></div>
      </>
    );
  }

  return (
    <>
      <Loading loading={loading} background="rgba(0,0,0,0.4)" loaderColor="#fff" />
      <HeaderSection title="My" heading="Resignation" />

      <div className="page-body">
        <div className="container-xl">

          {/* Show form modal */}
          {showForm && <ApplyResignationForm handleResignationModalClose={onFormClose} />}

          {/* No active resignation — show policy + apply button */}
          {canApply && !showForm && (
            <div className="card" style={{ borderRadius: 14, border: '1px solid #e2e8f0' }}>
              <div className="card-body" style={{ padding: '32px' }}>
                <div className="row align-items-center">
                  <div className="col-lg-8">
                    <h5 style={{ fontWeight: 700, color: '#0a1628', marginBottom: 12 }}>Resignation Policy</h5>
                    <ul style={{ color: '#64748b', fontSize: '0.88rem', lineHeight: 1.8, paddingLeft: 20 }}>
                      <li>During the notice period, employees are not allowed to take leaves.</li>
                      <li>Variable pay and leave encashment eligibility is voided upon resignation.</li>
                      <li>All company assets must be returned during the clearance process.</li>
                    </ul>
                  </div>
                  <div className="col-lg-4 text-end">
                    <button className="btn btn-primary" onClick={handleApplyClick} style={{ borderRadius: 8, fontWeight: 600, padding: '10px 24px' }}>
                      Apply for Resignation
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Previous withdrawn/rejected resignation details */}
          {canApply && !showForm && resignation && (
            <div className="mt-3">
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Previous Application</p>
              <ResignationCard resignation={resignation} getStatusIcon={getStatusIcon} getStatusStyle={getStatusStyle} finalStyle={finalStyle} />
            </div>
          )}

          {/* Active resignation */}
          {!canApply && !showForm && resignation && (
            <ResignationCard
              resignation={resignation}
              getStatusIcon={getStatusIcon}
              getStatusStyle={getStatusStyle}
              finalStyle={finalStyle}
              handleCancelResignation={handleCancelResignation}
              showWithdraw={resignation.final_status === "Pending"}
              isToken={isToken}
              onRefresh={fetchMyResignations}
            />
          )}

          {/* Previous resignation history */}
          {prevResignation && !showForm && (
            <div className="mt-4">
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Previous Application</p>
              <ResignationCard resignation={prevResignation} getStatusIcon={getStatusIcon} getStatusStyle={getStatusStyle} finalStyle={getStatusStyle(prevResignation.final_status)} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Resignation Detail Card Component
function ResignationCard({ resignation, getStatusIcon, getStatusStyle, finalStyle, handleCancelResignation, showWithdraw, isToken, onRefresh }) {
  const [infoResponse, setInfoResponse] = useState('');
  const [submittingInfo, setSubmittingInfo] = useState(false);
  const [infoFile, setInfoFile] = useState(null);

  if (!resignation) return null;
  const workflows = resignation.workflows || [];
  const comments = resignation.comments || [];

  // Check if there's a pending info request (last "Info Requested" comment without a following "Info Provided")
  const lastInfoRequest = [...comments].reverse().find(c => c.action === "Info Requested");
  const lastInfoProvided = [...comments].reverse().find(c => c.action === "Info Provided");
  const hasInfoRequest = lastInfoRequest && (!lastInfoProvided || new Date(lastInfoRequest.created_at) > new Date(lastInfoProvided.created_at));

  const handleSubmitInfo = async () => {
    const plain = infoResponse.replace(/<[^>]*>/g, '').trim();
    if (!plain) { toast.error("Please provide your response."); return; }
    setSubmittingInfo(true);
    try {
      const formData = new FormData();
      formData.append("resignation_id", resignation.id);
      formData.append("step_number", resignation.current_step);
      formData.append("action", "Info Provided");
      formData.append("comment", infoResponse);
      if (infoFile) formData.append("attachment", infoFile);

      const response = await axios.post(CONST.RESIGNATION_PROCESS, formData, {
        headers: { Authorization: `Bearer ${isToken}`, "Content-Type": "multipart/form-data" }
      });
      if (response.data.status === "success") {
        toast.success("Response submitted successfully.");
        setInfoResponse('');
        setInfoFile(null);
        if (onRefresh) onRefresh();
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to submit response.");
    }
    setSubmittingInfo(false);
  };
  const isWithdrawn = resignation.final_status === "Withdrawn";
  const isRejected = resignation.final_status === "Rejected";
  const isClosed = isWithdrawn || isRejected;

  const labelStyle = { fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 };
  const valueStyle = { fontSize: '0.92rem', fontWeight: 600, color: '#0a1628' };

  return (
    <div className="card" style={{ borderRadius: 14, border: '1px solid #e2e8f0' }}>
      <div className="card-body" style={{ padding: '24px' }}>
        <div className="row g-4">
          {/* Left — Details */}
          <div className="col-lg-7">
            {/* Status Banner */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h5 style={{ fontWeight: 700, color: '#0a1628', margin: 0 }}>Resignation Details</h5>
              <span style={{ background: finalStyle.bg, color: finalStyle.color, padding: '5px 16px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>
                {isWithdrawn ? "Withdrawn by Employee" : resignation.final_status}
              </span>
            </div>

            {/* Info Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: 20 }}>
              <div>
                <div style={labelStyle}>Subject</div>
                <div style={valueStyle}>{resignation.subject}</div>
              </div>
              <div>
                <div style={labelStyle}>Applied Date</div>
                <div style={valueStyle}>{moment(resignation.applied_date).format("DD MMM YYYY")}</div>
              </div>
              {!isClosed && (
                <>
                  <div>
                    <div style={labelStyle}>Notice Period</div>
                    <div style={valueStyle}>{resignation.notice_period || 0} Days</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Relieving Date</div>
                    <div style={{ ...valueStyle, color: resignation.release_date ? '#059669' : '#d97706' }}>
                      {resignation.release_date ? moment(resignation.release_date).format("DD MMM YYYY") : "Under Review"}
                    </div>
                  </div>
                </>
              )}
              {isClosed && (
                <div>
                  <div style={labelStyle}>{isWithdrawn ? "Withdrawn On" : "Rejected On"}</div>
                  <div style={valueStyle}>{moment(resignation.updated_at || resignation.applied_date).format("DD MMM YYYY")}</div>
                </div>
              )}
            </div>

            {/* Info Request Banner */}
            {hasInfoRequest && (
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ background: '#fde68a', color: '#92400e', width: 28, height: 28, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700 }}>!</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e' }}>Additional Information Requested</span>
                  </div>
                  <span style={{ fontSize: '0.72rem', color: '#b45309' }}>{moment(lastInfoRequest.created_at).format("DD MMM YYYY, hh:mm A")}</span>
                </div>

                {/* Message from HR */}
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Message from {lastInfoRequest.commenter?.first_name} {lastInfoRequest.commenter?.last_name}</div>
                  <div style={{ fontSize: '0.88rem', color: '#334155', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: lastInfoRequest.comment }} />
                </div>

                {/* Response Form */}
                {isToken && (
                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Your Response</div>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Type your response..."
                      value={infoResponse}
                      onChange={(e) => setInfoResponse(e.target.value)}
                      style={{ borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.85rem', marginBottom: 10 }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#1167e8', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem', padding: '6px 20px' }}
                        onClick={handleSubmitInfo}
                        disabled={submittingInfo}
                      >
                        {submittingInfo ? "Submitting..." : "Submit Response"}
                      </button>
                      <label style={{ fontSize: '0.82rem', color: '#1167e8', cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, margin: 0 }}>
                        <input type="file" style={{ display: 'none' }} accept="application/pdf,application/msword,image/*" onChange={(e) => {
                          if (e.target.files[0]) {
                            setInfoFile(e.target.files[0]);
                            toast.info("File attached: " + e.target.files[0].name);
                          }
                        }} />
                        {infoFile ? infoFile.name : "+ Attach File"}
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reason */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Reason</div>
              <div style={{ fontSize: '0.88rem', color: '#334155', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: resignation.reason }} />
            </div>

            {/* Withdraw button */}
            {showWithdraw && handleCancelResignation && (
              <div style={{ marginTop: 16 }}>
                <button className="btn" onClick={handleCancelResignation}
                  style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', padding: '8px 20px' }}>
                  Withdraw Application
                </button>
              </div>
            )}
          </div>

          {/* Right — Timeline */}
          <div className="col-lg-5">
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px' }}>
              <h6 style={{ fontWeight: 700, color: '#0a1628', marginBottom: 16 }}>Approval Timeline</h6>

              {workflows.sort((a, b) => a.step_number - b.step_number).map((w, i) => {
                const st = getStatusStyle(w.status);
                const isLast = i === workflows.length - 1;
                return (
                  <div key={w.id || i} style={{ display: 'flex', gap: 12, marginBottom: isLast ? 0 : 16, position: 'relative' }}>
                    {/* Line */}
                    {!isLast && (
                      <div style={{ position: 'absolute', left: 11, top: 28, bottom: -12, width: 2, background: '#e2e8f0' }} />
                    )}
                    {/* Icon */}
                    <div style={{ minWidth: 24, paddingTop: 2 }}>{getStatusIcon(w.status)}</div>
                    {/* Content */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0a1628' }}>{w.step_name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        {w.step_name !== "Applied" && (
                          <span style={{ background: st.bg, color: st.color, padding: '2px 10px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600 }}>
                            {w.status}
                          </span>
                        )}
                        {w.approved_at && (
                          <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{moment(w.approved_at).format("DD MMM YYYY")}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {workflows.length === 0 && (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem', padding: '16px 0' }}>No workflow steps found.</div>
              )}
            </div>
          </div>
        </div>

        {/* Audit Log */}
        {comments.length > 0 && (
          <div style={{ marginTop: 24, borderTop: '1px solid #e2e8f0', paddingTop: 20 }}>
            <h6 style={{ fontWeight: 700, color: '#0a1628', marginBottom: 12 }}>Audit Log</h6>
            <div className="table-responsive">
              <table className="table" style={{ fontSize: '0.85rem', margin: 0 }}>
                <thead>
                  <tr>
                    {['Name', 'Comment', 'Attachment', 'Date', 'Action'].map(h => (
                      <td key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', padding: '10px 14px', background: '#f1f5f9', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.04em' }}>{h}</td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...comments].reverse().map((c, i) => {
                    const actionStyle = getStatusStyle(c.action);
                    return (
                      <tr key={c.id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0a1628', fontSize: '0.85rem' }}>{c.commenter?.first_name} {c.commenter?.last_name}</td>
                        <td style={{ padding: '10px 14px', color: '#334155', maxWidth: 300, fontSize: '0.85rem' }} dangerouslySetInnerHTML={{ __html: c.comment || "—" }} />
                        <td style={{ padding: '10px 14px' }}>
                          {c.attachment ? (
                            <a href={CONST.HOST + c.attachment} target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', color: '#1167e8', fontWeight: 600, textDecoration: 'none' }}>View File</a>
                          ) : <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>—</span>}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{moment(c.created_at).format("DD MMM YYYY, hh:mm A")}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ background: actionStyle.bg, color: actionStyle.color, padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600 }}>{c.action}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Resignation;
